import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpButton from 'Library/GrpButton/GrpButton';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import { getTargetProfilesStatus, distributeProfile } from 'Services/distributionApi';
import classes from './TargetProfilesStatus.module.scss';

const StatusBadge = ({ status }) => {
  const { ReportApprovedCount, TotalScopeCount } = status;
  let badgeClass = classes.badgeDanger;
  if (ReportApprovedCount >= TotalScopeCount && TotalScopeCount > 0) {
    badgeClass = classes.badgeSuccess;
  } else if (ReportApprovedCount > 0) {
    badgeClass = classes.badgeWarning;
  }

  return (
    <span className={`${classes.badge} ${badgeClass}`}>
      {ReportApprovedCount} of {TotalScopeCount}
    </span>
  );
};

const TargetProfilesStatus = () => {
  const context = useContext(AppContext);
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishModal, setPublishModal] = useState({ visible: false, profile: null });

  const reportingDate = context.state.selectedReportingDate;
  const hasReportingPeriod = !!reportingDate?.code;

  const loadData = useCallback(async () => {
    if (!reportingDate?.code) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await getTargetProfilesStatus(reportingDate.code);
      const data = result?.Content || result?.value || result || [];
      setRowData(Array.isArray(data) ? data : []);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load statuses'}</> });
    } finally {
      setLoading(false);
    }
  }, [reportingDate, context.handlers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleView = (profileId) => {
    navigate(`/distribution/profile-preview/${profileId}`);
  };

  const handleShowPublish = (profile) => {
    setPublishModal({ visible: true, profile });
  };

  const handlePublish = async (partialDistribution) => {
    const profile = publishModal.profile;
    setPublishModal({ visible: false, profile: null });
    if (!profile) return;

    try {
      setLoading(true);
      await distributeProfile(profile.TargetProfileId, partialDistribution);
      context.handlers.setToast({
        type: 'success',
        body: <>Publication successfully requested.</>,
      });
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to publish'}</> });
      setLoading(false);
    }
  };

  const columnDefs = [
    {
      headerName: 'Target',
      field: 'TargetProfileName',
      width: 180,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Distribution Status',
      field: 'StatusName',
      width: 180,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Scope Status',
      width: 170,
      sortable: false,
      filter: false,
      cellRenderer: (params) => <StatusBadge status={params.data} />,
    },
    {
      headerName: 'Actions',
      sortable: false,
      filter: false,
      width: 200,
      cellRenderer: (params) => (
        <div className={classes.actions}>
          <GrpButton
            size="Small"
            text="View"
            onClick={() => handleView(params.data.TargetProfileId)}
          />
          <GrpButton
            size="Small"
            text="Publish"
            type="Primary"
            onClick={() => handleShowPublish(params.data)}
          />
        </div>
      ),
    },
  ];

  if (loading) {
    return <GrpSpinner />;
  }

  if (!hasReportingPeriod) {
    return (
      <div className={classes.container}>
        <p>
          Please select a reporting period to show the distribution statuses for that period.
        </p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={`ag-theme-reborn ${classes.gridWrapper}`}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
          }}
          pagination={true}
          paginationPageSize={50}
          domLayout="autoHeight"
        />
      </div>

      {/* Publish Modal */}
      {publishModal.visible && (
        <div className={classes.overlay} onClick={() => setPublishModal({ visible: false, profile: null })}>
          <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
            <h4>Publish profile &quot;{publishModal.profile?.TargetProfileName}&quot;</h4>
            {
              publishModal.profile?.DistributionEvents?.length > 0 && (
                <div className={classes.distributionEvents}>
                  <p>
                      <h7>Partial distribution detected, would you like to send the delta or everything?</h7><br />
                      {publishModal.profile?.DistributionEvents?.map((event, index) => (
                        <><h7 key={index}>{event.DistributionEventCreatedDateTime.toString("dd MMM yyyy")} - {event.DistributedArtifactCount} of {publishModal.profile?.DistributionTotal} reports sent</h7><br /></>
                      ))}
                  </p>
                  <div className={classes.radioGroup}>
                    <p>A partial distribution already exists. Choose distribution type:</p>
                    <label className={classes.radioLabel}>
                      <input type="radio" name="distType" value="delta" defaultChecked />
                        Delta (only new/changed documents)
                    </label>
                    <label className={classes.radioLabel}>
                      <input type="radio" name="distType" value="full" />
                      Full distribution
                    </label>
                  </div>
                </div>
              )
            }

            <div className={classes.readiness}>
              {
                publishModal.profile?.ReportApprovedCount === publishModal.profile?.TotalScopeCount
                && publishModal.profile?.TotalScopeCount > 0
                && (<><span className={classes.readyIcon}>✓ {publishModal.profile?.ReportApprovedCount} reports ready to publish.</span><br/></>)
              }

              {
                publishModal.profile?.ReportApprovedCount > 0 && publishModal.profile?.ReportApprovedCount < publishModal.profile?.TotalScopeCount
                  && (<><span className={classes.partiallyReadyIcon}>⚠ {publishModal.profile?.ReportApprovedCount}/{publishModal.profile?.ReportCount} reports ready.</span><br/></>)
              }
              {
                publishModal.profile?.OutOfScopeCount > 0
                  && (<><span className={classes.notReadyIcon}>⚠ {publishModal.profile?.OutOfScopeCount}/{publishModal.profile?.TotalScopeCount} reports are not in scope.</span><br/></>)
              }
              {
                publishModal.profile?.IsTargetProfileRestricted
                  && (<span className={classes.notReadyIcon}>⚠ Warning: Restricted share classes.</span>)
              }
            </div>

            <div className={classes.modalActions}>
              <GrpButton
                size="Medium"
                type="Primary"
                text="Publish"
                onClick={() => {
                  const radios = document.getElementsByName('distType');
                  let partial = true;
                  for (const r of radios) {
                    if (r.checked && r.value === 'full') partial = false;
                  }
                  handlePublish(partial);
                }}
              />
              <GrpButton
                size="Medium"
                type="Secondary"
                text="Cancel"
                onClick={() => setPublishModal({ visible: false, profile: null })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TargetProfilesStatus;
